import { autocomplete } from '@algolia/autocomplete-js';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import type { InstantSearch } from 'instantsearch.js';

import React, { createElement, Fragment } from 'react';
import { createRoot } from 'react-dom/client';
import {
  debouncedSetInstantSearchUiState,
  getInstantSearchUiState,
  setInstantSearchUiState,
} from './InstantSearch';
import { searchClient } from '../../state/SearchClient';

const { ALGOLIA_SEARCH_INDEX_NAME } = window as any;

const searchPageState = getInstantSearchUiState();

export type AutocompleteWidgetOptions = {
  hasHits?: boolean;
  helpCentre?: boolean;
  resultsUrl?: string;
};

export function startAutocomplete(
  container: HTMLElement,
  panelRootRef: React.MutableRefObject<any>,
  rootRef: React.MutableRefObject<HTMLElement>,
  instantSearch: InstantSearch,
  options: AutocompleteWidgetOptions,
) {
  let skipInstantSearchStateUpdate = false;
  const params = new URL(window.location.href).searchParams;

  const querySuggestionsPlugin = createQuerySuggestionsPlugin({
    searchClient,
    indexName: `${ALGOLIA_SEARCH_INDEX_NAME}_query_suggestions`,
    transformSource({ source }) {
      return {
        ...source,
        onSelect({ item }) {
          if (options.hasHits) {
            setInstantSearchUiState({
              query: item.query,
            });
          } else {
            // navigate to the /search results page with the query state
            let base = options.resultsUrl;

            // strip the query param if it's already there
            if (base.includes('?')) {
              base = base.substring(0, base.indexOf('?'));
            }

            window.location.href = `${base}?q=${encodeURI(item.query)}&within=${
              options.helpCentre ? 'help' : 'all'
            }`;
          }
        },
      };
    },
  });

  const search = autocomplete({
    container,
    placeholder: 'Type to search...',
    insights: true,
    detachedMediaQuery: 'none',
    renderer: { createElement, Fragment, render: () => {} },
    openOnFocus: true,

    render({ children }, root) {
      if (!panelRootRef.current || rootRef.current !== root) {
        rootRef.current = root;

        panelRootRef.current?.unmount();
        panelRootRef.current = createRoot(root);
      }

      panelRootRef.current.render(children);
    },
    plugins: [querySuggestionsPlugin],
    initialState: {
      query: searchPageState.query || params.get('q'),
    },
    onSubmit({ state }) {
      if (options.hasHits === true) {
        setInstantSearchUiState({
          query: state.query,
        });

        // update via pushState the q param
        window.history.pushState(
          {},
          '',
          `${window.location.pathname}?q=${encodeURI(state.query)}`,
        );
      } else {
        let base = options.resultsUrl;

        // strip the query param if it's already there
        if (base.includes('?')) {
          base = base.substring(0, base.indexOf('?'));
        }

        // navigate to the /search results page with the query state
        window.location.href = `${base}?q=${encodeURI(state.query)}&within=${
          options.helpCentre ? 'help' : 'all'
        }`;
      }
    },
    onReset() {
      setInstantSearchUiState({
        query: '',
      });
    },
    onStateChange({ prevState, state }) {
      // update the global search state if we're displaying hits.
      if (options.hasHits === true) {
        if (!skipInstantSearchStateUpdate && prevState.query !== state.query) {
          debouncedSetInstantSearchUiState({
            query: state.query,
          });
        }
        skipInstantSearchStateUpdate = false;
      }
    },
  });

  window.addEventListener('popstate', () => {
    skipInstantSearchStateUpdate = true;

    search.setQuery(
      (instantSearch.helper && instantSearch.helper.state.query) || '',
    );
  });

  return search;
}
